import React, { useState, useMemo } from 'react';
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
} from '@nextui-org/react';
import {
  formatMoneyNoCurrency,
  formatMoneyRMB,
  formatNumber,
} from '../../utils/functions';
import { SettlementDetails } from 'common/src/utils/types';
import { QUARTILE_COLORS } from 'common/src/utils/definitions';
import { getColor, getColorClass } from 'common/src/utils/functions';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';

interface Props {
  settlementDetails: SettlementDetails[];
}

const LiquidationDetailsTable: React.FC<Props> = ({
  settlementDetails = [],
}) => {
  const { t } = useTranslation('settlements');

  const sortedSettlements = useMemo(() => {
    return [...settlementDetails].sort((a, b) => {
      const aQuart = a.quartil_vs_market_eta;
      const bQuart = b.quartil_vs_market_eta;

      if (aQuart === null && bQuart === null) return 0;
      if (aQuart === null || aQuart === undefined) return 1;
      if (bQuart === null || bQuart === undefined) return -1;

      if (aQuart !== bQuart) return (aQuart as number) - (bQuart as number);

      const aDiff = a.difference_vs_market_eta;
      const bDiff = b.difference_vs_market_eta;
      const aPercentage = aDiff || 0;
      const bPercentage = bDiff || 0;
      return aPercentage - bPercentage;
    });
  }, [settlementDetails]);

  const totals = useMemo(() => {
    let totalDifferenceAbsolute = 0;
    let totalBultos = 0;
    let totalIngresoTotal = 0;

    sortedSettlements.forEach((item) => {
      // Sumar bultos
      if (typeof item.quantity === 'number' && isFinite(item.quantity)) {
        totalBultos += item.quantity;
      }

      // Sumar ingreso total
      if (typeof item.local_total_price === 'number' && isFinite(item.local_total_price)) {
        totalIngresoTotal += item.local_total_price;
      }

      // Calcular diferencia absoluta
      const diffPercentage = item.difference_vs_market_eta;
      const localTotalPrice = item.local_total_price;

      // Solo procesar si tenemos valores numéricos válidos
      if (typeof diffPercentage !== 'number' ||
        typeof localTotalPrice !== 'number' ||
        !isFinite(diffPercentage) ||
        !isFinite(localTotalPrice) ||
        localTotalPrice === 0) {
        return;
      }

      const percentage = diffPercentage / 100;
      const denominator = 1 + percentage;

      // Evitar división por cero
      if (denominator === 0 || !isFinite(denominator)) {
        return;
      }

      const marketPrice = localTotalPrice / denominator;
      const absoluteDifference = (marketPrice - localTotalPrice) * -1;

      // Solo sumar si el resultado es un número válido
      if (isFinite(absoluteDifference)) {
        totalDifferenceAbsolute += absoluteDifference;
      }
    });

    return {
      bultos: totalBultos,
      ingresoTotal: totalIngresoTotal,
      diferencia: totalDifferenceAbsolute
    };
  }, [sortedSettlements]);

  const renderTotalCell = (columnKey: string) => {
    switch (columnKey) {
      case 'pallet':
        return <p className='text-bold font-semibold'>{t('TOTAL')}</p>;
      case 'bultos':
        return <p className='text-sm font-semibold'>{totals.bultos}</p>;
      case 'total':
        return (
          <p className='text-sm font-semibold'>
            {formatMoneyRMB(totals.ingresoTotal, {
              currency: sortedSettlements[0]?.local_currency,
            })}
          </p>
        );
      case 'difference_absolute':
        return (
          <p
            className={`text-sm font-semibold ${totals.diferencia > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
              }`}
          >
            {formatMoneyNoCurrency(totals.diferencia)}
          </p>
        );
      default:
        return <p></p>;
    }
  };

  const renderCell = (item: SettlementDetails, columnKey: string) => {
    const getQuartileColor = (quartile: number | null) => {
      if (quartile === null) return 'bg-default-1000';
      return QUARTILE_COLORS[
        quartile.toString() as keyof typeof QUARTILE_COLORS
      ];
    };

    switch (columnKey) {
      case 'status':
        return (
          <div
            className={`w-3 h-3 rounded-full ${getQuartileColor(
              item.quartil_vs_market_eta as number | null
            )}`}
          ></div>
        );
      case 'pallet':
        return <p className='text-bold'>{item.pallet || 'N/A'}</p>;
      case 'variety':
        return <p className='text-sm'>{item.variety || 'N/A'}</p>;
      case 'format':
        return <p className='text-sm'>{item.format || 'N/A'}</p>;
      case 'caliber':
        return <p className='text-sm'>{`${item.caliber}`}</p>;
      case 'color':
        return <p className='text-sm'>{item.color || 'N/A'}</p>;
      case 'unit_weight':
        return <p className='text-sm'>{item.unit_weight || 'N/A'}</p>;
      case 'bultos':
        return <p className='text-sm'>{item.quantity || 'N/A'}</p>;
      case 'total':
        return (
          <p className='text-sm'>
            {formatMoneyRMB(item.local_total_price, {
              currency: item.local_currency,
            })}
          </p>
        );
      case 'price_box':
        return (
          <p className='text-sm'>
            {formatMoneyRMB(item.local_total_price / item.quantity, {
              currency: item.local_currency,
            })}
          </p>
        );
      case 'difference_percentage':
        return (
          <p
            className={`text-sm ${item?.difference_vs_market_eta &&
              (item?.difference_vs_market_eta as number) > 0 &&
              'text-green-600 dark:text-green-400'
              } ${item?.difference_vs_market_eta &&
              (item?.difference_vs_market_eta as number) < 0 &&
              'text-red-600 dark:text-red-400'
              }`}
          >
            {formatNumber(
              item.difference_vs_market_eta || 0,
              {
                decimals: 2,
              }
            )}
            %
          </p>
        );
      case 'difference_absolute':
        const diffPercentage = item.difference_vs_market_eta;
        const localTotalPrice = item.local_total_price;

        // Verificar que los valores sean números válidos
        if (typeof diffPercentage !== 'number' ||
          typeof localTotalPrice !== 'number' ||
          !isFinite(diffPercentage) ||
          !isFinite(localTotalPrice) ||
          localTotalPrice === 0) {
          return <p className='text-sm'>—</p>;
        }

        const percentage = diffPercentage / 100;
        const denominator = 1 + percentage;

        // Evitar división por cero
        if (denominator === 0 || !isFinite(denominator)) {
          return <p className='text-sm'>—</p>;
        }

        const marketPrice = localTotalPrice / denominator;
        const absoluteDifference = (marketPrice - localTotalPrice) * -1;

        // Verificar que el resultado sea válido
        if (!isFinite(absoluteDifference)) {
          return <p className='text-sm'>—</p>;
        }

        return (
          <p
            className={`text-sm ${getColorClass(diffPercentage)}`}
          >
            {formatMoneyNoCurrency(absoluteDifference)}
          </p>
        );
      default:
        return null;
    }
  };

  const columns = [
    { name: t('STATUS'), uid: 'status' },
    { name: t('PALLET'), uid: 'pallet' },
    { name: t('VARIEDAD'), uid: 'variety' },
    { name: t('FORMATO'), uid: 'format' },
    { name: t('CALIBRE'), uid: 'caliber' },
    { name: t('COLOR'), uid: 'color' },
    { name: t('PESO BULTO'), uid: 'unit_weight' },
    { name: t('BULTOS'), uid: 'bultos' },
    { name: t('PRECIO BULTO'), uid: 'price_box' },
    { name: t('INGRESO TOTAL'), uid: 'total' },
    { name: t('VS MERCADO %'), uid: 'difference_percentage' },
    {
      name: t('VS MERCADO ({currency})').replace('{currency}', settlementDetails[0]?.local_currency || ''),
      uid: 'difference_absolute',
    },
  ];

  return (
    <div>
      <Table
        removeWrapper
        aria-label={t('Tabla de Detalle de Liquidaciones')}
        className='shadow-none max-w-screen overflow-x-auto'
      >
        <TableHeader columns={columns}>
          {(column) => (
            <TableColumn key={column.uid}>{column.name}</TableColumn>
          )}
        </TableHeader>
        <TableBody>
          {sortedSettlements.map((item) => (
            <TableRow key={item.pallet}>
              {columns.map((column) => (
                <TableCell key={column.uid}>
                  {renderCell(item, column.uid)}
                </TableCell>
              ))}
            </TableRow>
          ))}
          <TableRow key="total" className="border-t-2 border-divider">
            {columns.map((column) => (
              <TableCell key={column.uid} className="bg-default-100">
                {renderTotalCell(column.uid)}
              </TableCell>
            ))}
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
};

export default LiquidationDetailsTable;
